<template>
  <div class="page page-video-detail">
    <LayoutContainer>
      <div class="page-header">
        <h1 class="title">
          <i class="fas fa-video"></i> วิดีโอ
        </h1>
        <div class="filter">
          <FilterDate :defaultFilter="'forever'" />
        </div>
      </div>
      <div class="page-header page-video-header">
        <h1 v-if="video" class="title">
          {{videoTitle}}
          <a :href="video.contentUrl" class="btn btn-link action">
            <i class="fas fa-external-link-alt"></i>
          </a>
        </h1>
        <p class="detail">Video ID : {{videoId}}</p>
      </div>
      <div class="page-content">
        <div class="row">
          <div class="col-xl-4 col-lg-12">
            <VideoMetaContainer
              :video="video"
              :error="state.video.error"
              :isPreLoaderShowing="state.video.isPreLoaderShowing"
              :isLoading="state.video.isLoading"
            />
          </div>
          <div class="col-xl-8 col-lg-12">
            <VideoStatsCard
              :statsData="videoDetail"
              :error="state.stats.error"
              :isPreLoaderShowing="state.stats.isPreLoaderShowing"
              :isLoading="state.stats.isLoading"
            />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-12">
            <RetentionSection
              :isLoading="state.retention.isLoading"
              :isPreLoading="state.retention.isPreLoaderShowing"
              :isOverdays="isOver30days"
              :retentiondata="graphData"
            />
          </div>
        </div>
      </div>
    </LayoutContainer>
  </div>
</template>

<script>
/* eslint-disable arrow-parens */
import _ from 'lodash';
import { subDays, differenceInCalendarDays } from 'date-fns';
import { loadVideoDetail, loadRetentionGraph, loadVideoStats } from './api';
import dateFormatter from '@/utils/dateFormatter';
import numberUtils from '@/utils/numberUtils';
import generateLink from '@/utils/generateLink';
import FilterDate from '@/components/FilterDate.vue';
import VideoMetaContainer from '@/video/VideoMetaContainer.vue';
import VideoStatsCard from '@/video/VideoStatsCard.vue';
import LayoutContainer from '@/layout/LayoutContainer.vue';
import RetentionSection from '@/video/RetentionSection.vue';

export default {
  mixins: [dateFormatter, numberUtils, generateLink],
  data() {
    return {
      video: null,
      videoDetail: {
        bigCard: ['', ''],
        smallCard: ['', '', '', ''],
      },
      graphData: null,
      state: {
        video: {
          isPreLoaderShowing: true,
          isLoading: false,
          error: null,
        },
        stats: {
          isPreLoaderShowing: true,
          isLoading: false,
          error: null,
        },
        retention: {
          isPreLoaderShowing: true,
          isLoading: false,
          error: null,
        },
      },
      error: null,
    };
  },
  components: {
    LayoutContainer,
    FilterDate,
    RetentionSection,
    VideoStatsCard,
    VideoMetaContainer,
  },
  watch: {
    '$route.query': {
      handler() {
        const filter = this.generateQuery(this.$route.query);
        this.load(filter);
        this.loadVideoStatistics(filter);
        this.loadRetention(filter);
      },
    },
  },
  computed: {
    videoId() {
      return this.$route.params.videoId;
    },
    videoTitle() {
      return _.get(this.video, 'title', `Video #${this.videoId}`);
    },
    last30days() {
      if (this.$route.query.endDate) {
        return this.transformDateToDisplay(
          subDays(new Date(this.$route.query.endDate), 30),
        );
      }
      return this.transformDateToDisplay(subDays(new Date(), 30));
    },
    isOver30days() {
      if (
        differenceInCalendarDays(
          new Date(this.$route.query.endDate),
          new Date(this.$route.query.startDate),
        ) > 30
      ) {
        return true;
      }
      return false;
    },
  },
  created() {
    const filter = this.generateQuery(this.$route.query);
    this.load(filter);
    this.loadVideoStatistics(filter);
    this.loadRetention(filter);
  },
  methods: {
    async load(filter) {
      this.state.video.isLoading = true;
      try {
        const response = await loadVideoDetail(this.videoId, filter);
        this.state.video.isPreLoaderShowing = false;
        this.state.video.isLoading = false;
        this.video = response.data;
      } catch (error) {
        this.video = {
          videoId: this.videoId,
          title: `Video #${this.videoId}`,
          coverImageUrl: null,
          contentUrl: null,
        };
        this.error = error;
      }
    },
    async loadVideoStatistics(filter) {
      this.state.stats.isLoading = true;
      try {
        const response = await loadVideoStats(this.videoId, filter);
        const bigCardData = [
          {
            key: 'totalplays',
            name: 'Total Plays',
            value: response.data.totalPlay,
            unit: 'plays',
            unitName: 'Plays',
          },
          {
            key: 'atleast10secplays',
            name: 'At least 10s plays',
            value: response.data.total10SecondsPlay,
            unit: 'plays',
            unitName: 'Plays',
          },
        ];
        const smallCardData = [
          {
            key: 'totalreach',
            name: 'Total Reach',
            value: response.data.totalReach,
            unit: 'any',
          },
          {
            key: 'totalwatchduration',
            name: 'Total Watch Duration',
            value: this.transformSecond(
              response.data.totalWatchDurationInSeconds,
            ),
            unit: 'min',
            unitName: 'min.',
          },
          {
            key: 'avgwatchduration',
            name: 'Average Watch Duration',
            value: this.transformSecond(
              response.data.avgWatchDurationInSeconds,
            ),
            unit: 'min',
            unitName: 'min.',
          },
          {
            key: 'avgwatchdurationpencent',
            name: 'Average Watch Duration',
            value: response.data.avgWatchDurationInPercent,
            unit: 'percent',
            unitName: '%',
          },
        ];
        this.videoDetail.bigCard = { ...bigCardData };
        this.videoDetail.smallCard = { ...smallCardData };
        this.state.stats.isPreLoaderShowing = false;
        this.state.stats.isLoading = false;
      } catch (error) {
        this.state.stats.error = error.response;
        this.state.stats.isPreLoaderShowing = false;
        this.state.stats.isLoading = false;
      }
    },
    async loadRetention(filter) {
      this.state.retention.isLoading = true;
      this.graphData = null;
      const response = await loadRetentionGraph(this.videoId, filter);
      const result = _.map(response.data.data, (data) => ({
        x: data.timeInSeconds,
        y: data.retentionPercent,
      }));
      this.graphData = [
        {
          name: '',
          data: await result,
        },
      ];
      this.state.retention.isPreLoaderShowing = false;
      this.state.retention.isLoading = false;
    },
    transformSecond(second) {
      const minutes = Math.floor(second / 60);
      const seconds = second - minutes * 60;
      let secs = Math.floor(seconds);
      if (secs < 10) {
        secs = `0${secs}`;
      }
      const returnText = `${minutes}:${secs}`;
      if (returnText.length > 5) {
        return this.transformUnit(parseFloat(returnText));
      }
      return `${minutes}:${secs}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import '@/assets/scss/themes/components/page.scss';
@import '@/assets/scss/themes/components/card.scss';

.video-stats-container {
  display: flex;
  flex-direction: row;

  .video-stats-item {
    flex: 1;
    margin-right: $spacer;

    &:last-child {
      margin-right: 0;
    }
  }
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
